import { useDispatch, useSelector } from "react-redux";
import BreadCrumbs from "../../components/General/BreadCrumbs";
import InputField from "../../components/UI/Inputs/InputField";
import Button from "../../components/UI/Button";
import { selectUser, setUserData } from "../../store/userSlice";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Modal from "../../components/UI/Modal";
import modalStyle from "../../components/Vehicles/RequestVehicle/index.module.css";
import style from "./index.module.css";
import { Success, Warning } from "../../components/Icons";
import { updateProfile } from "../../utils/fetcher";
import { rawPhoneNumber, useScrollToTop } from "../../utils/generalUtils";
import { changeUserPassword, sendVerificationMessage, updateUserPhone, verifyUserPhone } from "../Sign/utils";
import InputCode from "../../components/UI/Inputs/InputCode";
import { useTranslation } from "../../hooks/useTranslation";

const Profile = ({ history }: any) => {
  const { user } = useSelector(selectUser);
  const { locale } = useTranslation();

  useScrollToTop();

  const dispatch = useDispatch();

  const [submittingState, setSubmittingState] = useState<{
    isSubmitting: boolean;
    error: string;
    submitSuccess: boolean;
  }>({
    isSubmitting: false,
    error: "",
    submitSuccess: false,
  });

  const { handleSubmit, watch, register, setValue, formState } = useForm<any>({
    defaultValues: {
      name: user?.name || "",
      profile_picture: "",
      phone: +rawPhoneNumber(user?.phone || ""),
      update_phone: +rawPhoneNumber(user?.phone || ""),
      update_verify_code: "",
      old_password: "",
      new_password: "",
      confirm_new_password: "",
    },
  });

  const watchedInputs = watch();

  const [updatePhoneState, setUpdatePhoneState] = useState<{
    showModal: boolean;
    activePage: number;
  }>({ showModal: false, activePage: 0 });

  const [updatePasswordState, setUpdatePasswordState] = useState<{
    showModal: boolean;
  }>({ showModal: false });

  useEffect(() => {
    if (!user) {
      history.replace("/sign");
    }
  }, [user, history]);

  const onUpdateProfileHandler = useCallback(
    async (type: "general" | "phone" | "password", data: any) => {
      setSubmittingState({
        isSubmitting: true,
        error: "",
        submitSuccess: false,
      });
      let error = "",
        success = false;
      if (type === "general") {
        await updateProfile({
          name: data.name,
          ...(data.profile_picture?.length ? { profile_picture: data.profile_picture[0] } : {}),
        })
          .then((res) => {
            if (res?.[0]?.status === 200) {
              success = true;
            }
          })
          .catch((error) => {
            error = error?.response?.data?.message || locale("error_occured");
          });
      } else if (type === "phone") {
        if (updatePhoneState.activePage === 0) {
          await sendVerificationMessage("+20" + rawPhoneNumber(data.update_phone))
            .then((res) => {
              if (res.status === 200) {
                setUpdatePhoneState({ activePage: 1, showModal: true });
              }
            })
            .catch((err) => {
              error = err?.response?.data?.message || locale("error_occured");
            });
        } else {
          await verifyUserPhone("+20" + rawPhoneNumber(data.update_phone), data.update_verify_code)
            .then(async (res) => {
              if (res.status === 200) {
                await updateUserPhone("+20" + +rawPhoneNumber(data.update_phone), data.update_verify_code)
                  .then(() => {
                    success = true;
                    dispatch(
                      setUserData({
                        phone: "+20" + rawPhoneNumber(data.update_phone),
                      }),
                    );
                    setValue("phone", +rawPhoneNumber(data.update_phone));
                    setValue("update_phone", +rawPhoneNumber(data.update_phone));
                    setValue("update_verify_code", "");
                    setUpdatePhoneState({ activePage: 0, showModal: false });
                  })
                  .catch((err) => {
                    error = err?.response?.data?.message || locale("error_occured");
                  });
              }
            })
            .catch((err) => {
              error = err?.response?.data?.message || locale("error_occured");
            });
        }
      } else if (type === "password") {
        await changeUserPassword(data.old_password, data.new_password)
          .then((res) => {
            if (res.status === 200) {
              success = true;
              setUpdatePasswordState({ showModal: false });
            }
          })
          .catch((err) => {
            error = err?.response?.data?.message || locale("error_occured");
          });
      }
      setSubmittingState({
        isSubmitting: false,
        error,
        submitSuccess: success,
      });
      if (success && data?.profile_picture?.[0]) window.location.reload();
    },
    [locale, updatePhoneState, setValue, dispatch],
  );

  return user ? (
    <>
      {!submittingState.error && updatePasswordState.showModal && (
        <Modal
          onClose={() => {
            setUpdatePasswordState({ showModal: false });
            setValue("old_password", "");
            setValue("new_password", "");
            setValue("confirm_new_password", "");
          }}
        >
          <div className={style.modal}>
            <p className={style.modal_title}>{locale("change_password")}</p>
            <form onSubmit={handleSubmit((data) => onUpdateProfileHandler("password", data))}>
              <div className={style.modal_inputs}>
                <InputField label={locale("current_password")} type="password" extraProps={register("old_password")} />
                <InputField label={locale("new_password")} type="password" extraProps={register("new_password")} />
                <InputField
                  label={locale("confirm_password")}
                  type="password"
                  extraProps={register("confirm_new_password", {
                    validate: (value) => value === watchedInputs.new_password || locale("passwords_dont_match"),
                  })}
                  error={
                    typeof formState.errors?.confirm_new_password?.message === "string"
                      ? formState.errors?.confirm_new_password?.message
                      : undefined
                  }
                />
              </div>
              <div className={style.modal_ctas}>
                <Button
                  styleType="primary_black"
                  buttonType="submit"
                  disabled={submittingState.isSubmitting}
                  isLoading={submittingState.isSubmitting}
                >
                  {locale("update")}
                </Button>
              </div>
            </form>
          </div>
        </Modal>
      )}
      {!submittingState.error && updatePhoneState.showModal && (
        <Modal
          onClose={() => {
            setUpdatePhoneState({ activePage: 0, showModal: false });
            setValue("update_phone", +rawPhoneNumber(user.phone));
            setValue("update_verify_code", "");
          }}
        >
          <div className={style.modal}>
            <p className={style.modal_title}>{locale(updatePhoneState.activePage === 0 ? "edit_phone" : "verify")}</p>
            {updatePhoneState.activePage === 1 && <p>{locale("we_sent_code")}</p>}
            <form onSubmit={handleSubmit((data) => onUpdateProfileHandler("phone", data))}>
              <div className={style.modal_inputs}>
                {updatePhoneState.activePage === 0 ? (
                  <InputField
                    label={locale("phone")}
                    placeholder={locale("phone")}
                    type="phone"
                    extraProps={register("update_phone", {
                      required: locale("required_field"),
                    })}
                    error={
                      typeof formState.errors?.update_phone?.message === "string"
                        ? formState.errors?.update_phone?.message
                        : undefined
                    }
                  />
                ) : (
                  <InputCode onChange={(value: string) => setValue("update_verify_code", value)} />
                )}
              </div>
              <div className={style.modal_ctas}>
                <Button
                  styleType="primary_black"
                  buttonType="submit"
                  disabled={submittingState.isSubmitting}
                  isLoading={submittingState.isSubmitting}
                >
                  {locale(updatePhoneState.activePage === 0 ? "send" : "edit")}
                </Button>
              </div>
            </form>
          </div>
        </Modal>
      )}
      {(submittingState.submitSuccess || submittingState.error) && (
        <Modal
          onClose={() =>
            setSubmittingState({
              isSubmitting: false,
              error: "",
              submitSuccess: false,
            })
          }
        >
          <div className={modalStyle.modal}>
            <div className={modalStyle.modal_response}>
              {submittingState.submitSuccess ? (
                <Success width="50" height="50" color="#35b545" />
              ) : (
                <Warning width="50" height="50" color="#dc0000" />
              )}
              <p>{(submittingState.submitSuccess ? locale("updated_successfuly") : "") || submittingState.error}</p>
            </div>
          </div>
        </Modal>
      )}
      {/* <div className="main_section">
        <div className="container-lg">
          <div className="section_content_padding">
            <BreadCrumbs
              pages={[
                { text: locale("home"), link: "/" },
                {
                  text: locale("edit_profile"),
                  link: "",
                },
              ]}
            />
          </div>
        </div>
      </div> */}
      <div className="main_section">
        <div className="container-lg">
          <div className="section_content_padding">
            <h3 className="main_section__title main_section__title--center">{locale("edit_profile")}</h3>
            <form
              onSubmit={handleSubmit((data) => onUpdateProfileHandler("general", data))}
              className={style.profile_section}
            >
              <div className={style.profile_image}>
                <img
                  src={
                    watchedInputs.profile_picture && watchedInputs.profile_picture.length > 0
                      ? window.URL.createObjectURL(watchedInputs.profile_picture[0])
                      : user.profile_picture
                  }
                  alt=""
                />
                <input
                  id="profile_pic_upload"
                  type="file"
                  accept=".png, .jpeg, .jpg"
                  {...register("profile_picture")}
                />
                <label htmlFor="profile_pic_upload">{locale("change_profile_pic")}</label>
              </div>
              <div className={style.profile_fields}>
                <InputField type="text" label={locale("name")} extraProps={register("name")} />
                <div className={style.profile_fields__single}>
                  <button
                    type="button"
                    className={style.profile_fields__single_btn}
                    onClick={() => {
                      setUpdatePhoneState({ activePage: 0, showModal: true });
                    }}
                  >
                    {locale("edit")}
                  </button>
                  <InputField
                    type="phone"
                    label={locale("phone")}
                    extraProps={{ ...register("phone"), disabled: true }}
                  />
                </div>
                {/* <InputSelect
                  label={locale(city}
                  props={{}}
                  watchedInputs={{}}
                  selectOptions={[]}
                  siteLang={siteLang}
                  extraProps={register('name')}
                /> */}
              </div>
              <div className={style.profile_cta}>
                <Button
                  styleType="secondary"
                  buttonType="button"
                  onClick={() => setUpdatePasswordState({ showModal: true })}
                >
                  {locale("change_password")}
                </Button>
                <Button
                  styleType="primary_black"
                  buttonType="submit"
                  disabled={submittingState.isSubmitting}
                  isLoading={submittingState.isSubmitting}
                >
                  {locale("save_changes")}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  ) : (
    <></>
  );
};

export default Profile;
